import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Heading, Paragraph } from '@digdir/designsystemet-react';
import { getMessages } from '../../../components/SchedulingSettingsForm/constants';
import { TeamSettingsLayout } from '../../../components/TeamSettingsLayout';
import { useGetEffectiveTeamSchedulingSettings } from '../../../hooks/queries/useGetEffectiveTeamSchedulingSettings';
import { useGetTeamSchedulingSettings } from '../../../hooks/queries/useGetTeamSchedulingSettings';
import { useUpdateTeamSchedulingSettingsMutation } from '../../../hooks/mutations/useUpdateTeamSchedulingSettingsMutation';
import { useDeleteTeamSchedulingSettingsMutation } from '../../../hooks/mutations/useDeleteTeamSchedulingSettingsMutation';
import { Spinner } from '../../../components/Spinner/Spinner';
import { WorkingHoursForm } from '../../../components/WorkingHoursForm';
import classes from './TeamSchedulingPage.module.css';

const Message = ({ color, title, text }: { color: 'danger' | 'warning'; title: string; text: string }) => (
  <Alert data-color={color}>
    <Heading level={2} data-size="2xs" className={classes.alertHeading}>
      {title}
    </Heading>
    <Paragraph>{text}</Paragraph>
  </Alert>
);

export const TeamSchedulingPage = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();

  const numericTeamId = parseInt(teamId || '', 10);
  const isValidTeamId = !!teamId && !isNaN(numericTeamId) && numericTeamId > 0;

  const {
    data: effectiveSettings,
    isLoading: isLoadingEffective,
    error: effectiveError,
  } = useGetEffectiveTeamSchedulingSettings(numericTeamId);
  const { data: teamSettings } = useGetTeamSchedulingSettings(numericTeamId);

  const saveMutation = useUpdateTeamSchedulingSettingsMutation(numericTeamId);
  const deleteMutation = useDeleteTeamSchedulingSettingsMutation(numericTeamId);

  useEffect(() => {
    if (saveMutation.isSuccess) {
      toast.success(getMessages().SUCCESS.SAVED);
    }
  }, [saveMutation.isSuccess]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      toast.success(getMessages().SUCCESS.DELETED);
    }
  }, [deleteMutation.isSuccess]);

  const content = () => {
    if (!isValidTeamId) {
      return (
        <Message
          color="danger"
          title={t('teamScheduling.errors.invalidTeamId.title')}
          text={t('teamScheduling.errors.invalidTeamId.description')}
        />
      );
    }
    if (isLoadingEffective) {
      return (
        <div className={classes.loadingContainer}>
          <Spinner label={t('teamScheduling.loading')} />
        </div>
      );
    }
    if (effectiveError) {
      return (
        <Message
          color="danger"
          title={t('teamScheduling.errors.loadError.title')}
          text={t('teamScheduling.errors.loadError.description')}
        />
      );
    }
    if (!effectiveSettings) {
      return (
        <Message
          color="warning"
          title={t('teamScheduling.errors.noSettings.title')}
          text={t('teamScheduling.errors.noSettings.description')}
        />
      );
    }
    return (
      <WorkingHoursForm
        effectiveSettings={effectiveSettings}
        hasCustomSettings={!!teamSettings}
        saveMutation={saveMutation}
        deleteMutation={deleteMutation}
      />
    );
  };

  return <TeamSettingsLayout>{content()}</TeamSettingsLayout>;
};
